import { useState } from "react";
import axios from "axios";
import { BsStarFill, BsStar } from "react-icons/bs";

function StarPicker({ value, onChange }) {
  const [hovered, setHovered] = useState(0);
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(star)}
          onMouseEnter={() => setHovered(star)}
          onMouseLeave={() => setHovered(0)}
          className="text-2xl transition-transform hover:scale-110 focus:outline-none"
        >
          {(hovered || value) >= star ? (
            <BsStarFill className="text-amber-400" />
          ) : (
            <BsStar className="text-gray-300" />
          )}
        </button>
      ))}
    </div>
  );
}

const LABELS = { 1: "Mauvais", 2: "Décevant", 3: "Correct", 4: "Très bien", 5: "Excellent" };

// Props :
//   reservationId  — _id de la réservation (pour soumettre)
//   reviewId       — _id de l'avis (pour modifier, mode edit)
//   hotelName      — nom de l'hôtel
//   initialRating  — note existante (mode edit)
//   initialComment — commentaire existant (mode edit)
//   onSuccess      — callback après succès
//   mode           — "create" | "edit"
export default function ReviewForm({
  reservationId,
  reviewId,
  hotelName,
  initialRating = 0,
  initialComment = "",
  onSuccess,
  mode = "create",
}) {
  const [rating, setRating] = useState(initialRating);
  const [comment, setComment] = useState(initialComment);
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const isEdit = mode === "edit";

  const handleSubmit = async () => {
    if (rating === 0) {
      setErrorMsg("Veuillez sélectionner une note.");
      return;
    }

    try {
      setStatus("loading");
      setErrorMsg("");

      if (isEdit) {
        await axios.put(
          `https://api.komorotel.com/api/reviews/${reviewId}/edit`,
          { rating, comment },
          { withCredentials: true }
        );
      } else {
        await axios.post(
          "https://api.komorotel.com/api/reviews",
          { reservationId, rating, comment },
          { withCredentials: true }
        );
      }

      setStatus("success");
      onSuccess?.({ rating, comment });
    } catch (err) {
      setErrorMsg(err.response?.data?.message || "Une erreur est survenue. Réessayez.");
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <div className="flex flex-col items-center gap-2 py-6 text-center">
        <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center text-green-500 text-2xl">
          ✓
        </div>
        <p className="font-medium text-gray-800">
          {isEdit ? "Avis modifié !" : "Merci pour votre avis !"}
        </p>
        {!isEdit && (
          <p className="text-sm text-gray-500">
            Il sera visible après validation par notre équipe.
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex flex-col gap-4">
      <div>
        <p className="text-sm font-semibold text-gray-800">
          {isEdit ? "Modifier votre avis" : "Laisser un avis"}
        </p>
        {hotelName && <p className="text-xs text-gray-400 mt-0.5">{hotelName}</p>}
      </div>

      <div className="flex flex-col gap-1">
        <StarPicker value={rating} onChange={setRating} />
        {rating > 0 && (
          <span className="text-xs text-amber-500 font-medium">{LABELS[rating]}</span>
        )}
      </div>

      <textarea
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        maxLength={1000}
        rows={4}
        placeholder="Partagez votre expérience (optionnel)..."
        className="w-full text-sm text-gray-700 placeholder-gray-300 border border-gray-200 rounded-xl px-4 py-3 resize-none focus:outline-none focus:ring-2 focus:ring-[#3A9D23]/20 focus:border-[#3A9D23]/50 transition"
      />
      <div className="text-right text-xs text-gray-300">{comment.length}/1000</div>

      {errorMsg && <p className="text-xs text-red-500">{errorMsg}</p>}

      <button
        onClick={handleSubmit}
        disabled={status === "loading"}
        className="w-full py-3 rounded-xl bg-[#3A9D23] text-white text-sm font-medium hover:bg-[#2e8019] transition disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {status === "loading"
          ? "Envoi en cours..."
          : isEdit
          ? "Sauvegarder les modifications"
          : "Envoyer mon avis"}
      </button>
    </div>
  );
}